<script setup lang="ts">
import t from "~/utils/translate";
const router = useRouter();
const route = useRoute();
import api from "~/utils/auth/api";
import { useQuery } from "@tanstack/vue-query";
import LeagueAccordion from "~/components/layout/TrackRacing/Shared/Event/LeagueAccordion.vue";
import Selections from "./Selections.vue";
import { parseContent } from "~/utils/kentico/ContentParser";
import { IKenticoResponse } from "~/types/kentico/content";

interface IJackpotResponse {
	Id: string;
	jackpotName: string;
	date: string;
	isAbandoned: boolean;
	isActive: boolean;
	isCompleted: boolean;
	isToday: boolean;
}

const isSelectionView = computed(() => {
	return !!route.query.jackpotName && !!route.query.jackpotDate;
});

const isToday = (jackpotDate: string) => {
	const selectedDate = new Date(jackpotDate);
	const today = new Date();
	return today.setHours(0, 0, 0, 0) === selectedDate.setHours(0, 0, 0, 0);
};

const jackpotApiCall = async () => {
	const response = await api<IJackpotResponse[]>({
		url: `/Jackpot/JackpotsWithEmop?countryCode=ZA`,
		type: "horses",
		apiVersion: "v1",
		options: { method: "GET" },
	});
	if (!Array.isArray(response)) return [];
	return response.map((jackpot) => ({ ...jackpot, isToday: isToday(jackpot.date) }));
};
const { data: jackpots } = useQuery({
	queryKey: ["Jackpot"],
	queryFn: jackpotApiCall,
});

const { data: jackpotTermsAndConditions, isLoading } = useQuery<string | null>({
	queryKey: ["horses-jackpot-terms"],
	queryFn: () =>
		api<IKenticoResponse>({
			url: `?host={kenticoHost}&route=/Content/SouthAfrica/Horse-Racing-Jackpot-Ts-Cs&lang=en`,
			type: "content",
		}).then(async (res) => {
			const parsedContent = parseContent(res.content, {
				applyBindings: true,
			});
			return parsedContent?.content || "";
		}),
});
</script>

<template>
	<div>
		<div v-if="!isSelectionView">
			<div class="text-center text-sm leading-tight my-4" v-html="t('jackpot-description')" />

			<LeagueAccordion
				:regionLabel="t('today-races')"
				:showRegionFlag="false"
				:default-value="true"
				class="rounded-lg bg-light-300 dark:bg-dark-800 my-2"
			>
				<div class="p-2">
					<div class="grid grid-cols-1 gap-2">
						<div class="text-center text-sm" v-if="!jackpots?.some((j) => j.isToday)">{{ t("no-jackpot-available") }}</div>
						<div
							v-else
							v-for="jackpot in jackpots?.filter((j) => j.isToday)"
							class="h-[40px] justify-between capitalize bg-light-50 dark:bg-dark-700 flex items-center p-2 text-xs w-full truncate rounded-md font-bold text-dark-400 dark:text-light-50"
						>
							<div>
								<span class="text-dark-800 dark:text-light-50"> {{ jackpot.jackpotName }}, </span>
								<span class="font-light">
									<UiDateTime dateFormat="EEEE" :date="jackpot.date" />
								</span>
							</div>

							<UiButton
								:label="jackpot.isAbandoned ? t('race-abandoned') : t('enter-jackpot')"
								size="small"
								variant="primary"
								:disabled="jackpot.isAbandoned"
								class="h-6 min-w-[90px] my-1 text-xs prima"
								@click="router.push({ query: { ...route.query, jackpotName: jackpot.jackpotName, jackpotDate: jackpot.date } })"
							/>
						</div>
					</div>
				</div>
			</LeagueAccordion>

			<LeagueAccordion
				:regionLabel="t('upcoming-races')"
				:showRegionFlag="false"
				:default-value="true"
				class="rounded-lg bg-light-300 dark:bg-dark-800 my-2"
			>
				<div class="p-2">
					<div class="grid grid-cols-1 gap-2">
						<div class="text-center text-sm" v-if="jackpots?.filter((j) => !j.isToday).length === 0">{{ t("no-jackpot-available") }}</div>
						<div
							v-else
							v-for="jackpot in jackpots?.filter((j) => !j.isToday)"
							class="h-[40px] justify-between capitalize bg-light-50 dark:bg-dark-700 flex items-center p-2 text-xs w-full truncate rounded-md font-bold text-dark-400 dark:text-light-50"
						>
							<div>
								<span class="text-dark-800 dark:text-light-50"> {{ jackpot.jackpotName }}, </span>
								<span class="font-light">
									<UiDateTime dateFormat="EEEE" :date="jackpot.date" />
								</span>
							</div>

							<UiButton
								:label="jackpot.isAbandoned ? t('race-abandoned') : t('view')"
								size="small"
								variant="custom"
								:disabled="jackpot.isAbandoned"
								class="h-6 min-w-[90px] my-1 text-xs text-light-50 bg-dark-700 dark:bg-dark-800 hover:bg-dark-600"
								@click="router.push({ query: { ...route.query, jackpotName: jackpot.jackpotName, jackpotDate: jackpot.date } })"
							/>
						</div>
					</div>
				</div>
			</LeagueAccordion>

			<LeagueAccordion
				:regionLabel="t('terms-and-conditions')"
				:showRegionFlag="false"
				:default-value="true"
				class="rounded-lg bg-light-300 dark:bg-dark-800 my-2"
			>
				<div class="p-2">
					<div class="text-sm" v-html="jackpotTermsAndConditions" />
				</div>
			</LeagueAccordion>
		</div>
		<Selections v-else />
	</div>
</template>
